import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateStoreModeUrl } from "../../../api/endpoints";
import { setStore } from "../../../slice/storeSlice";

const StoreModeToggle = () => {
  const [mode, setMode] = useState("retail"); // 'retail' or 'supermarket'
  const { store } = useSelector((state) => state.store);

  useEffect(() => {
    store.superMarketMode ? setMode("supermarket") : setMode("retail");
  }, [store]);

  useEffect(() => {}, [mode]);

  const dispatch = useDispatch();

  const updateStoreMode = async (newMode) => {
    try {
      let response = await fetch(updateStoreModeUrl + store._id, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({ mode: newMode }),
      });
      if (!response.ok) {
        throw new Error("Failed to update store mode");
      }

      let data = await response.json();
      dispatch(setStore(data));
    } catch (error) {
      console.error("Error updating store mode:", error);
    }
  };

  const styles = {
    container: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
      border: "1px solid #f3f4f6",
      padding: "24px",
    },
    headerWrapper: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      marginBottom: "24px",
    },
    headerContent: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    iconBox: {
      flexShrink: 0,
      width: "40px",
      height: "40px",
      backgroundColor: "#a855f7",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    iconSvg: {
      width: "20px",
      height: "20px",
      color: "#ffffff",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2.5",
      strokeLinecap: "round",
      strokeLinejoin: "round",
    },
    titleBlock: {
      display: "flex",
      flexDirection: "column",
    },
    titleText: {
      fontSize: "16px",
      fontWeight: 600,
      color: "#111827",
      lineHeight: "1.5",
      margin: 0,
    },
    subtitleText: {
      fontSize: "14px",
      color: "#6b7280",
      margin: "4px 0 0 0",
      lineHeight: "1.4",
    },
    toggleSwitch: {
      position: "relative",
      display: "inline-flex",
      height: "28px",
      width: "56px",
      flexShrink: 0,
      alignItems: "center",
      borderRadius: "9999px",
      transition: "all 0.2s ease-in-out",
      cursor: "pointer",
      border: "none",
      outline: "none",
      backgroundColor: mode === "retail" ? "#3b82f6" : "#d1d5db",
    },
    toggleKnob: {
      display: "inline-block",
      height: "20px",
      width: "20px",
      transform: mode === "retail" ? "translateX(26px)" : "translateX(0px)",
      borderRadius: "9999px",
      backgroundColor: "#ffffff",
      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
      transition: "transform 0.2s ease-in-out",
    },
    cardsContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    cardWrapper: {
      position: "relative",
      borderRadius: "12px",
      border: "2px solid",
      transition: "all 0.2s ease-in-out",
      cursor: "pointer",
    },
    retailCardActive: {
      borderColor: "#93c5fd",
      backgroundColor: "#dbeafe",
    },
    retailCardInactive: {
      borderColor: "#e5e7eb",
      backgroundColor: "#ffffff",
    },
    supermarketCardActive: {
      borderColor: "#d1d5db",
      backgroundColor: "#f9fafb",
    },
    supermarketCardInactive: {
      borderColor: "#e5e7eb",
      backgroundColor: "#ffffff",
    },
    cardContent: {
      padding: "20px",
    },
    cardInnerFlex: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: "16px",
    },
    cardLeftSection: {
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      flex: 1,
      minWidth: 0,
    },
    cardIconBox: {
      flexShrink: 0,
      width: "32px",
      height: "32px",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    iconRetailActive: {
      backgroundColor: "#3b82f6",
    },
    iconInactive: {
      backgroundColor: "#9ca3af",
    },
    iconSvgSmall: {
      width: "16px",
      height: "16px",
      color: "#ffffff",
      fill: "currentColor",
    },
    cardTextWrapper: {
      flex: 1,
      minWidth: 0,
    },
    cardTitleRow: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "8px",
    },
    cardTitle: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#111827",
      margin: 0,
    },
    activeBadge: {
      padding: "2px 8px",
      fontSize: "12px",
      fontWeight: 500,
      borderRadius: "6px",
      color: "#1d4ed8",
      backgroundColor: "#dbeafe",
    },
    cardDescription: {
      fontSize: "14px",
      color: "#374151",
      lineHeight: "1.6",
      margin: 0,
    },
    descriptionBold: {
      fontWeight: 600,
      color: "#111827",
    },
    radioWrapper: {
      flexShrink: 0,
      paddingTop: "4px",
    },
    radioButton: {
      width: "22px",
      height: "22px",
      borderRadius: "9999px",
      border: "2px solid",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.25s ease-in-out",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    },
    radioRetailActive: {
      borderColor: "#3b82f6",
      backgroundColor: "#3b82f6",
      boxShadow: "0 2px 4px 0 rgba(59, 130, 246, 0.3)",
    },
    radioSupermarketActive: {
      borderColor: "#6b7280",
      backgroundColor: "#6b7280",
      boxShadow: "0 2px 4px 0 rgba(107, 114, 128, 0.3)",
    },
    radioInactive: {
      borderColor: "#d1d5db",
      backgroundColor: "#ffffff",
    },
    radioDot: {
      width: "10px",
      height: "10px",
      borderRadius: "9999px",
      backgroundColor: "#ffffff",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.1)",
    },
    systemBanner: {
      marginTop: "24px",
      borderRadius: "12px",
      padding: "16px",
      border: "1px solid #fed7aa",
      backgroundColor: "#ffedd5",
    },
    bannerInnerFlex: {
      display: "flex",
      gap: "12px",
    },
    bannerIconWrapper: {
      flexShrink: 0,
    },
    bannerIconSvg: {
      width: "20px",
      height: "20px",
      color: "#ea580c",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
    },
    bannerContent: {
      flex: 1,
      minWidth: 0,
    },
    bannerTitle: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#111827",
      margin: "0 0 4px 0",
    },
    bannerText: {
      fontSize: "14px",
      color: "#374151",
      margin: 0,
      lineHeight: "1.5",
    },
  };

  return (
    <div style={styles.container}>
      {/* Header Section */}
      <div style={styles.headerWrapper}>
        <div style={styles.headerContent}>
          <div style={styles.iconBox}>
            <svg style={styles.iconSvg} viewBox='0 0 24 24'>
              <path d='M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z' />
            </svg>
          </div>
          <div style={styles.titleBlock}>
            <h3 style={styles.titleText}>Store Operation Mode</h3>
            <p style={styles.subtitleText}>Configure pricing behavior at checkout</p>
          </div>
        </div>

        {/* Mode Toggle Switch */}
        <button
          onClick={() => {
            setMode(mode === "retail" ? "supermarket" : "retail");
            updateStoreMode(mode === "retail" ? "supermarket" : "retail");
          }}
          style={styles.toggleSwitch}
          role='switch'
          aria-checked={mode === "retail"}
          aria-label='Toggle store operation mode'
        >
          <span style={styles.toggleKnob} />
        </button>
      </div>

      {/* Mode Cards */}
      <div style={styles.cardsContainer}>
        {/* Retail Mode Card */}
        <div
          onClick={() => setMode("retail")}
          style={{
            ...styles.cardWrapper,
            ...(mode === "retail" ? styles.retailCardActive : styles.retailCardInactive),
          }}
        >
          <div style={styles.cardContent}>
            <div style={styles.cardInnerFlex}>
              <div style={styles.cardLeftSection}>
                <div
                  style={{
                    ...styles.cardIconBox,
                    ...(mode === "retail" ? styles.iconRetailActive : styles.iconInactive),
                  }}
                >
                  <svg style={styles.iconSvgSmall} viewBox='0 0 20 20'>
                    <path d='M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z' />
                  </svg>
                </div>
                <div style={styles.cardTextWrapper}>
                  <div style={styles.cardTitleRow}>
                    <h4 style={styles.cardTitle}>Retail Mode</h4>
                    {mode === "retail" && <span style={styles.activeBadge}>Active</span>}
                  </div>
                  <p style={styles.cardDescription}>
                    Unit prices <span style={styles.descriptionBold}>can be adjusted</span> during product scan at checkout. Staff can modify prices for
                    discounts, price matching, or corrections.
                  </p>
                </div>
              </div>

              {/* Radio Button */}
              <div style={styles.radioWrapper}>
                <div
                  style={{
                    ...styles.radioButton,
                    ...(mode === "retail" ? styles.radioRetailActive : styles.radioInactive),
                  }}
                >
                  {mode === "retail" && <div style={styles.radioDot}></div>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Supermarket Mode Card */}
        <div
          onClick={() => setMode("supermarket")}
          style={{
            ...styles.cardWrapper,
            ...(mode === "supermarket" ? styles.supermarketCardActive : styles.supermarketCardInactive),
          }}
        >
          <div style={styles.cardContent}>
            <div style={styles.cardInnerFlex}>
              <div style={styles.cardLeftSection}>
                <div style={{ ...styles.cardIconBox, ...styles.iconInactive }}>
                  <svg style={styles.iconSvgSmall} viewBox='0 0 20 20'>
                    <path
                      fillRule='evenodd'
                      d='M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div style={styles.cardTextWrapper}>
                  <div style={styles.cardTitleRow}>
                    <h4 style={styles.cardTitle}>Supermarket Mode</h4>
                  </div>
                  <p style={styles.cardDescription}>
                    Unit prices <span style={styles.descriptionBold}>cannot be edited</span> during product scan at checkout. Prices are locked to prevent
                    manual adjustments during sales.
                  </p>
                </div>
              </div>

              {/* Radio Button */}
              <div style={styles.radioWrapper}>
                <div
                  style={{
                    ...styles.radioButton,
                    ...(mode === "supermarket" ? styles.radioSupermarketActive : styles.radioInactive),
                  }}
                >
                  {mode === "supermarket" && <div style={styles.radioDot}></div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System-Wide Setting Banner */}
      <div style={styles.systemBanner}>
        <div style={styles.bannerInnerFlex}>
          <div style={styles.bannerIconWrapper}>
            <svg style={styles.bannerIconSvg} viewBox='0 0 24 24'>
              <path d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
            </svg>
          </div>
          <div style={styles.bannerContent}>
            <p style={styles.bannerTitle}>System-Wide Setting</p>
            <p style={styles.bannerText}>
              {mode === "retail"
                ? "Price editing is currently enabled on all POS terminals. Staff can modify prices during checkout transactions."
                : "Price editing is currently disabled on all POS terminals. Products will scan at their preset prices only."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreModeToggle;
